import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Types
export interface User {
  id: string;
  email: string;
  isVerified: boolean;
  createdAt: string;
}

export interface UsageInfo {
  dailyLimit: number;
  usedToday: number;
  remainingToday: number;
  lastResetDate: string;
}

export interface PremiumAccessInfo {
  expiryDate: string;
  daysRemaining: number;
}

export interface AuthState {
  user: User | null;
  usage: UsageInfo | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  isPremium: boolean;
  premiumAccess: PremiumAccessInfo | null;
}

export interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  checkUsagePermission: () => Promise<{ canUse: boolean; needsAuth: boolean; remainingToday?: number; message?: string }>;
  activateWithCode: (code: string, email?: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  checkPremiumStatus: () => Promise<{ isPremium: boolean; premiumAccess: PremiumAccessInfo | null }>;
  refreshPremiumStatus: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    usage: null,
    token: localStorage.getItem('auth_token'),
    loading: true,
    isAuthenticated: false,
    isPremium: false,
    premiumAccess: null
  });

  // API base URL
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);


  // Initialize authentication state
  const initializeAuth = async () => {
    let token = localStorage.getItem('auth_token');

    // In development mode, set default dev token if none exists
    if (!token && (import.meta.env.DEV || window.location.hostname === 'localhost')) {
      token = 'dev-token-bx23r';
      localStorage.setItem('auth_token', token);
    }

    if (!token) {
      setAuthState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Verify token and get user profile
      const response = await fetch(`${API_BASE}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check premium status after profile is fetched
        let isPremium = false;
        let premiumAccess = null;
        try {
          const premiumResponse = await fetch(`${API_BASE}/payment/subscription-status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (premiumResponse.ok) {
            const premiumData = await premiumResponse.json();
            isPremium = premiumData.data?.isPremium || false;
            if (isPremium && premiumData.data) {
              premiumAccess = {
                expiryDate: premiumData.data.expiryDate,
                daysRemaining: premiumData.data.daysRemaining
              };
            }
          }
        } catch (premiumError) {
          console.warn('Failed to check premium status:', premiumError);
        }
        
        setAuthState({
          user: data.data.user,
          usage: data.data.usage,
          token,
          loading: false,
          isAuthenticated: true,
          isPremium,
          premiumAccess
        });
      } else {
        // Token is invalid
        localStorage.removeItem('auth_token');
        setAuthState({
          user: null,
          usage: null,
          token: null,
          loading: false,
          isAuthenticated: false,
          isPremium: false,
          premiumAccess: null
        });
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      localStorage.removeItem('auth_token');
      setAuthState({
        user: null,
        usage: null,
        token: null,
        loading: false,
        isAuthenticated: false,
        isPremium: false,
        premiumAccess: null
      });
    }
  };

  // Login function
  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('auth_token', data.data.token);
        setAuthState({
          user: data.data.user,
          usage: null, // Will be loaded by refreshProfile
          token: data.data.token,
          loading: false,
          isAuthenticated: true,
          isPremium: false,
          premiumAccess: null
        });
        
        // Load usage info and premium status
        await refreshProfile();
        
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error during login' };
    }
  };

  // Register function
  const register = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('auth_token', data.data.token);
        setAuthState({
          user: data.data.user,
          usage: data.data.usage || { dailyLimit: 20, usedToday: 0, remainingToday: 20 },
          token: data.data.token,
          loading: false,
          isAuthenticated: true,
          isPremium: false, // New users are not premium by default
          premiumAccess: null
        });

        // Refresh profile to get latest data (but we already have usage from registration response)
        // This is a backup in case we need updated info
        refreshProfile().catch(err => {
          console.warn('Profile refresh after registration failed (non-critical):', err);
        });

        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Network error during registration' };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('auth_token');
    setAuthState({
      user: null,
      usage: null,
      token: null,
      loading: false,
      isAuthenticated: false,
      isPremium: false,
      premiumAccess: null
    });
  };

  // Refresh user profile and usage info
  const refreshProfile = async () => {
    const token = authState.token || localStorage.getItem('auth_token');

    if (!token) return;

    try {
      // Add timeout to prevent indefinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${API_BASE}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setAuthState(prev => ({
          ...prev,
          user: data.data.user,
          usage: data.data.usage,
          isPremium: data.data.user?.isPremium || false,
          premiumAccess: data.data.premiumAccess || null
        }));
      } else {
        // Set default usage to prevent indefinite loading
        setAuthState(prev => ({
          ...prev,
          usage: { dailyLimit: 20, remainingToday: 20, usedToday: 0, lastResetDate: new Date().toISOString() }
        }));
      }
    } catch (error) {
      console.error('Profile refresh error:', error);
      // Set default usage to prevent indefinite loading
      setAuthState(prev => ({
        ...prev,
        usage: { dailyLimit: 20, remainingToday: 20, usedToday: 0, lastResetDate: new Date().toISOString() }
      }));
    }
  };

  // Check usage permission
  const checkUsagePermission = async () => {
    const token = authState.token || localStorage.getItem('auth_token');
    const sessionId = localStorage.getItem('session_id') || 'anonymous';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/usage-permission`, {
        headers
      });

      const data = await response.json();

      if (data.success) {
        return {
          canUse: data.canUse,
          needsAuth: data.needsAuth || false,
          remainingToday: data.remainingToday,
          message: data.message || (data.needsAuth ? 'Please sign in to use recommendations' : undefined)
        };
      } else {
        return {
          canUse: false,
          needsAuth: true,
          remainingToday: 0,
          message: data.message || 'Unable to check usage permission'
        };
      }
    } catch (error) {
      console.error('Usage permission check error:', error);
      return {
        canUse: false,
        needsAuth: false,
        remainingToday: 0,
        message: 'Network error while checking usage permission'
      };
    }
  };

  // Activate with code
  const activateWithCode = async (code: string, email?: string) => {
    try {
      const token = authState.token || localStorage.getItem('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Include Authorization header if user is authenticated
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/auth/activate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          activationCode: code,
          email: email?.trim() || undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        // If user data returned, update auth state
        if (data.data.user && data.data.token) {
          localStorage.setItem('auth_token', data.data.token);
          setAuthState({
            user: data.data.user,
            usage: null, // Will be refreshed
            token: data.data.token,
            loading: false,
            isAuthenticated: true,
            isPremium: false,
            premiumAccess: null
          });
        }
        
        // Always refresh profile to get updated usage info (including premium status)
        await refreshProfile();
        
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Activation failed' };
      }
    } catch (error) {
      console.error('Activation error:', error);
      return { success: false, error: 'Network error during activation' };
    }
  };

  // Check premium status
  const checkPremiumStatus = async (): Promise<{ isPremium: boolean; premiumAccess: PremiumAccessInfo | null }> => {
    const token = authState.token || localStorage.getItem('auth_token');

    if (!token) {
      return { isPremium: false, premiumAccess: null };
    }

    try {
      const response = await fetch(`${API_BASE}/payment/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const isPremium = data.data?.isPremium || false;
        let premiumAccess: PremiumAccessInfo | null = null;
        
        if (isPremium && data.data) {
          premiumAccess = {
            expiryDate: data.data.expiryDate,
            daysRemaining: data.data.daysRemaining
          };
        }

        return { isPremium, premiumAccess };
      }
      
      return { isPremium: false, premiumAccess: null };
    } catch (error) {
      console.error('Check premium status error:', error);
      return { isPremium: false, premiumAccess: null };
    }
  };

  // Refresh premium status (updates auth state)
  const refreshPremiumStatus = async () => {
    const { isPremium, premiumAccess } = await checkPremiumStatus();
    setAuthState(prev => ({
      ...prev,
      isPremium,
      premiumAccess
    }));
  };

  const contextValue: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    refreshProfile,
    checkUsagePermission,
    activateWithCode,
    checkPremiumStatus,
    refreshPremiumStatus
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;